import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Heart, Check, Gift, ArrowLeft, Crown, Star, Loader2 } from 'lucide-react';

const GIFT_PLANS = [
  { id: 'basic_gift_monthly', name: 'Basic', period: 'month', price: 9.99, display: '$9.99', subtitle: '1 month of Basic', features: ['Care circles beyond 5 people', 'Circle management', 'Tasks & calendar', 'Feed & updates'] },
  { id: 'basic_gift', name: 'Basic', period: 'year', price: 100.00, display: '$100', subtitle: '1 year of Basic — two months free', badge: 'Best value', features: ['Care circles beyond 5 people', 'Circle management', 'Tasks & calendar', 'Feed & updates'] },
];

const GiftPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('choose');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleCheckout = async () => {
    if (!recipientName.trim() || !recipientEmail.trim()) {
      toast.error('Please fill in the recipient details');
      return;
    }
    if (!senderName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setCheckoutLoading(true);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/billing/gift-checkout`, {
        plan_id: selectedPlan.id,
        price: selectedPlan.price,
        recipient_name: recipientName,
        recipient_email: recipientEmail,
        sender_name: senderName,
        message: message,
        origin_url: originUrl
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally { setCheckoutLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-12" data-testid="gift-page">
      <div className="max-w-3xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <button onClick={() => step === 'details' ? setStep('choose') : navigate('/individuals')} className="absolute top-5 left-5 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="relative w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-2 border-cyan-300/40" />
            <div className="absolute inset-2 rounded-full border-2 border-cyan-400/60" />
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
              <Heart className="w-4 h-4 text-white" fill="currentColor" />
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Gift className="w-6 h-6 inline-block mr-2 text-cyan-600" />
            Give the gift of CircleUpSquad<sup className="text-[8px] text-muted-foreground align-super">™</sup>
          </h1>
          <p className="text-muted-foreground">Help someone you care about get the support they need.</p>
        </div>

        {step === 'choose' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {GIFT_PLANS.map((plan) => (
              <Card key={plan.id} className="surface-card relative overflow-hidden hover:border-cyan-400/50 transition-colors cursor-pointer" onClick={() => { setSelectedPlan(plan); setStep('details'); }}>
                {plan.badge && (
                  <div className={`absolute top-0 right-0 text-white text-xs font-bold px-3 py-1 rounded-bl-lg ${plan.badge === 'Premium' ? 'bg-gradient-to-r from-cyan-500 to-emerald-500' : 'bg-cyan-500'}`}>
                    {plan.badge === 'Premium' ? <Crown className="w-3 h-3 inline mr-1" /> : <Star className="w-3 h-3 inline mr-1" />}
                    {plan.badge}
                  </div>
                )}
                <CardContent className="pt-7 pb-5 px-5">
                  <h3 className="text-lg font-bold text-foreground">{plan.name}</h3>
                  <p className="text-xs text-muted-foreground mb-3">{plan.subtitle}</p>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-2xl font-bold text-foreground">{plan.display}</span>
                  </div>
                  <ul className="space-y-2 mb-4">
                    {plan.features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Check className="w-3.5 h-3.5 text-cyan-500 flex-shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full btn-primary" data-testid={`gift-${plan.id}`}>
                    Give this gift
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {step === 'details' && selectedPlan && (
          <div className="max-w-md mx-auto">
            <Card className="surface-card">
              <CardContent className="pt-6 pb-6 px-6 space-y-5">
                <div className="bg-cyan-50 dark:bg-cyan-900/20 rounded-xl p-4 text-center">
                  <p className="text-sm font-semibold text-foreground">{selectedPlan.name}</p>
                  <p className="text-xs text-muted-foreground">{selectedPlan.subtitle}</p>
                  <p className="text-xl font-bold text-foreground mt-1">{selectedPlan.display}</p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-foreground">Who is this gift for?</h3>
                  <div className="space-y-2">
                    <Label htmlFor="recipient-name">Recipient's Name</Label>
                    <Input id="recipient-name" placeholder="Their name" value={recipientName} onChange={(e) => setRecipientName(e.target.value)} data-testid="recipient-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="recipient-email">Recipient's Email</Label>
                    <Input id="recipient-email" type="email" placeholder="their@email.com" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} data-testid="recipient-email-input" />
                  </div>
                </div>

                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-foreground">From</h3>
                  <div className="space-y-2">
                    <Label htmlFor="sender-name">Your Name</Label>
                    <Input id="sender-name" placeholder="Your name" value={senderName} onChange={(e) => setSenderName(e.target.value)} data-testid="sender-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gift-message">Personal message (optional)</Label>
                    <Input id="gift-message" placeholder="Thinking of you..." value={message} onChange={(e) => setMessage(e.target.value)} data-testid="gift-message-input" />
                  </div>
                </div>

                <Button className="w-full btn-primary h-12 text-base" onClick={handleCheckout} disabled={checkoutLoading} data-testid="gift-checkout-btn">
                  {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Gift className="w-4 h-4 mr-2" />}
                  Proceed to Payment
                </Button>

                <button onClick={() => setStep('choose')} className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors text-center">
                  Choose a different plan
                </button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftPage;
